/**
 * When a shipping method is worth saving, and when it isn't.
 *
 * Both delivery surfaces — the create wizard and the delivery editor — used to
 * carry their own copy of these rules, and they drifted: the wizard called a
 * method "offered" on a price alone, then silently dropped it at payload time
 * for having no days. One definition, imported by both.
 *
 * The shape is the three text fields every delivery form edits, so it fits the
 * wizard's `DeliveryValues` and the editor's `Slot` alike.
 */
export interface DeliveryDraft {
	/**
	 * What the client pays for this method — one figure, entered directly.
	 *
	 * It was a shipping price plus an admin fee, summed on the server into the
	 * price actually quoted. The fee is gone: it was invisible in every margin
	 * number on the platform, and product tiers already expressed the same idea
	 * as margin, so the split bought nothing and cost a second field.
	 */
	price: string;
	deliveryDaysMin: string;
	deliveryDaysMax: string;
}

/** Note "0" counts: free shipping is a price, not an absent one. */
export const filled = (v: string) => v.trim() !== '';

/** Nothing entered at all — "leave blank to skip a method" means this. */
export const isDeliveryBlank = (d: DeliveryDraft | undefined) =>
	!d || (!filled(d.price) && !filled(d.deliveryDaysMin) && !filled(d.deliveryDaysMax));

/**
 * A lead time is a range, so the days are written as a pair — half of one would
 * project an arrival nobody agreed to. Both set, or neither.
 */
const daysPaired = (d: DeliveryDraft) => filled(d.deliveryDaysMin) === filled(d.deliveryDaysMax);

/**
 * Priced, but the forwarder hasn't answered yet. This saves — the day columns
 * are nullable — it just can't be sent to a client until they're filled in.
 */
export const isDeliveryDaysUnknown = (d: DeliveryDraft | undefined) =>
	!!d && filled(d.price) && !filled(d.deliveryDaysMin) && !filled(d.deliveryDaysMax);

/**
 * Can be written. The price is what makes a delivery row exist; the days may be
 * settled later, so a priced method with no days is a legitimate save.
 */
export const isDeliveryOffered = (d: DeliveryDraft | undefined) =>
	!!d && filled(d.price) && daysPaired(d);

/**
 * Started but not writable — days with no price, or one half of the day pair.
 * Silently dropping it would throw away what the admin typed, so it's surfaced
 * on the method and holds up the save until it's resolved.
 */
export const isDeliveryIncomplete = (d: DeliveryDraft | undefined) =>
	!isDeliveryBlank(d) && !isDeliveryOffered(d);

/** Why a started method can't be written yet — shown on the method itself. */
export const deliveryIncompleteReason = (d: DeliveryDraft) =>
	!filled(d.price)
		? 'This method needs a shipping price. Clear the fields to skip it.'
		: 'Set both day bounds, or clear both to leave the lead time open.';

/** Days as the API wants them: the number, or null while the lead time is open. */
export const deliveryDayOrNull = (v: string) => {
	if (!filled(v)) return null;
	const n = parseInt(v, 10);
	return Number.isFinite(n) ? n : null;
};

/** A money field as the API wants it; blank or junk reads as 0. */
export const deliveryMoney = (v: string) => {
	const n = parseFloat(v);
	return Number.isFinite(n) ? n : 0;
};
